import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertCircle, ArrowRight } from "lucide-react";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Design tokens taken directly from the mock ───────────────────── */
const C = {
  ink: "#18181b",
  z500: "#71717a",
  z400: "#a1a1aa",
  z300: "#d4d4d8",
  z200: "#e4e4e7",
  z150: "#eeeeef",
  z100: "#f4f4f5",
  green: "#16a34a",
  blue: "#2563eb",
  orange: "#ea580c",
  red: "#dc2626",
  amber: "#d97706",
};
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/* Number formatter — thin space thousands, no trailing zeros. */
const fmt = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Math.round(n).toLocaleString("en-IN");
};
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Math.round(n)}%`);

/* Revenue formatter — ₹X.XX Cr (2 decimals) for cards / cut totals.
 * Feb 2026 revenue view. `n` is raw rupees. */
const fmtCr = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const cr = n / 1e7;
  return `₹${cr.toFixed(2)} Cr`;
};
/* Rupee-per-unit (ASP). */
const fmtRupees = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

/* ── Small primitives ─────────────────────────────────────────────── */
const MonoNum = ({ value, size = 40, color = C.ink }) => (
  <div
    className="font-semibold"
    style={{
      fontFamily: MONO,
      fontSize: size,
      letterSpacing: "-1px",
      color,
      lineHeight: 1.05,
    }}
  >
    {fmt(value)}
  </div>
);

const Joint = ({ pct, delta, tone = "ink", deltaFmt = fmt }) => {
  // pct: primary conversion % (colored amber if <80, red if <60), delta: leakage number (red)
  const pctColor =
    tone === "ink" ? C.ink : pct !== null && pct < 60 ? C.red : pct !== null && pct < 80 ? C.amber : C.ink;
  return (
    <div className="text-center" style={{ padding: "34px 28px 0" }}>
      <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: pctColor }} data-testid="pulse-joint-pct">
        {fmtPct(pct)}
      </div>
      <div style={{ color: C.z300, margin: "2px 0", display: "flex", justifyContent: "center" }}>
        <ArrowRight size={16} strokeWidth={2} />
      </div>
      <div style={{ fontFamily: MONO, fontSize: 11.5, color: C.red }} data-testid="pulse-joint-delta">
        {delta !== null && delta !== undefined && delta > 0 ? `−${deltaFmt(delta)}` : "—"}
      </div>
    </div>
  );
};

const FunnelBox = ({ label, code, value, valueRaw = false, valueColor = C.ink, subLeft, subRight, testId, onClick, badge, footnote, accent, pivot, gmLine }) => (
  <div
    style={{
      cursor: onClick ? "pointer" : "default",
      // Aug 2026 (page layout spec) — Ready pivot gets an orange
      // 1.5px border + faint tint; Dispatched / Awaiting outcome
      // cards get a 3px left-accent bar (orange / gray) via the
      // `accent` prop.
      padding: pivot ? "14px 16px" : (accent ? "10px 12px 10px 14px" : 0),
      border: pivot ? `1.5px solid ${C.orange}` : "none",
      borderRadius: pivot ? 10 : 0,
      background: pivot ? "#fffaf5" : "transparent",
      borderLeft: accent ? `3px solid ${accent}` : (pivot ? `1.5px solid ${C.orange}` : "none"),
    }}
    onClick={onClick}
    data-testid={testId}
  >
    <div style={{ fontSize: 12, color: C.z500, marginBottom: 10 }}>
      {label}{" "}
      <span style={{ fontFamily: MONO, color: C.z400 }}>{code}</span>
      {badge && <span style={{ color: C.orange, fontSize: 11, marginLeft: 6 }}>{badge}</span>}
    </div>
    {valueRaw ? (
      <div style={{ fontFamily: MONO, fontSize: 26, fontWeight: 300, color: valueColor, letterSpacing: -0.5 }}>
        {value}
      </div>
    ) : (
      <MonoNum value={value} color={valueColor} />
    )}
    {/* Feb 2026 — GM per stage. Only rendered in revenue mode
        (parent decides via `gmLine`). Muted, compact so it doesn't
        compete with the primary stage figure. */}
    {gmLine && (
      <div
        style={{ fontFamily: MONO, fontSize: 11.5, color: C.z500, marginTop: 4, letterSpacing: 0.2 }}
        data-testid={testId ? `${testId}-gm` : undefined}
      >
        GM <span style={{ color: C.ink }}>{gmLine.value}</span>
        <span style={{ color: C.z300 }}> · </span>
        <span style={{ color: (gmLine.pct >= 0 ? C.green : C.red) }}>{gmLine.pct == null ? "—" : `${gmLine.pct.toFixed(1)}%`}</span>
      </div>
    )}
    <div style={{ fontFamily: MONO, fontSize: 12, marginTop: 8 }}>
      {subLeft !== undefined && subLeft !== null && (
        <>
          <span style={{ color: C.green }}>{subLeft}</span>
          <span style={{ color: C.z300 }}> · </span>
          <span style={{ color: C.blue }}>{subRight}</span>
        </>
      )}
      {subLeft === undefined && subRight && <span style={{ color: C.z400 }}>{subRight}</span>}
    </div>
    {footnote && (
      <div
        style={{ fontSize: 11, color: C.amber, marginTop: 6, lineHeight: 1.4 }}
        data-testid={testId ? `${testId}-footnote` : undefined}
      >
        {footnote}
      </div>
    )}
  </div>
);

/* ── Legend line ──────────────────────────────────────────────────── */
const Legend = ({ revenue = false }) => (
  <div
    style={{
      fontSize: 12,
      color: C.z500,
      padding: "12px 0",
      borderTop: `1px solid ${C.z150}`,
      borderBottom: `1px solid ${C.z150}`,
      marginBottom: 40,
      lineHeight: 1.8,
    }}
    data-testid="pulse-legend"
  >
    <span style={{ color: C.z400, marginRight: 10 }}>How to read{revenue ? " · revenue" : ""}</span>
    <b style={{ color: C.ink }}>F</b> {revenue ? "forecast ₹ at plan price" : "forecast"} ·{" "}
    <b style={{ color: C.green }}>I</b> {revenue ? "month-opening" : "opening inventory (month-start)"} ·{" "}
    <b style={{ color: C.blue }}>S</b> scheduled ·{" "}
    <b style={{ color: C.blue }}>P</b> produced ·{" "}
    <b style={{ color: C.ink }}>RDY</b> ready = I + P (cumulative) ·{" "}
    <b style={{ color: C.ink }}>Awaiting</b> = min(F, RDY) − D ·{" "}
    <b style={{ color: C.amber || "#d97706" }}>Excess build</b> = RDY − max(F, D) ·{" "}
    <b style={{ color: C.orange }}>D</b> dispatched{revenue ? " ₹ at " : ""}
    {revenue && <u>actual invoiced</u>}
    {revenue ? " rate " : " "}
    <span style={{ color: C.z400 }}>
      {revenue
        ? "F/S/P/RDY priced from price master (customer × SKU) · D from Zoho invoices · "
        : "Supply = I + S · red = leakage · D/F can exceed 100%"}
      {revenue && (
        <span
          data-testid="pulse-legend-est-note"
          style={{
            fontSize: 10, padding: "1px 6px", borderRadius: 3,
            background: "#fef3c7", color: C.amber, fontFamily: MONO,
            fontWeight: 600, textTransform: "lowercase", marginLeft: 4,
          }}
        >est</span>
      )}
      {revenue && " = price fell back to last known"}
    </span>
  </div>
);

/* ── Month toggle — data-driven directory from /api/pulse/available-months ─── */
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthKeyToOption(key) {
  return {
    key,
    label: MONTH_LABELS[parseInt(key.slice(5, 7), 10) - 1],
    year: parseInt(key.slice(0, 4), 10),
  };
}
function currentMonthKey() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
}
const Toggle = ({ options, value, onChange, testIdPrefix }) => (
  <div
    style={{
      display: "inline-flex",
      borderRadius: 8,
      border: `1px solid ${C.z200}`,
      overflow: "hidden",
      background: "#fff",
    }}
  >
    {options.map((o) => {
      const active = o.key === value;
      return (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          data-testid={`${testIdPrefix}-${o.key}`}
          style={{
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 500,
            background: active ? C.ink : "#fff",
            color: active ? "#fff" : C.z500,
            border: "none",
            cursor: "pointer",
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

/* ── Bar chart (day/week) ─────────────────────────────────────────── */
const BarChart = ({ bars, mode, todayISO, valueKey = "units" }) => {
  const isRev = valueKey === "revenue";
  const fmtVal = isRev ? fmtCr : fmt;
  const values = bars.map((b) => b[valueKey] || 0);
  const max = Math.max(1, ...values);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: mode === "day" ? 8 : 16, height: 120 }}>
      {bars.map((b, i) => {
        const isToday = mode === "day" && b.date === todayISO;
        const isCurrentMonthBar = mode === "day" ? b.month_key === todayISO?.slice(0, 7) : true;
        const missing = b[valueKey] === null || b[valueKey] === undefined;
        const h = missing ? 1 : Math.max(1, Math.round((b[valueKey] / max) * 100));
        const bg = missing ? "transparent" : isCurrentMonthBar ? C.orange : C.z200;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, justifyContent: "flex-end" }}>
            <span style={{ fontFamily: MONO, fontSize: 10.5, color: missing ? C.z400 : C.z500 }}>
              {missing ? "—" : fmtVal(b[valueKey])}{b.is_mtd ? " · MTD" : ""}
            </span>
            {missing ? (
              <div style={{ width: "100%", maxWidth: mode === "day" ? 44 : 56, borderTop: `1px solid ${C.z200}`, height: 1 }} />
            ) : (
              <div
                style={{
                  width: "100%",
                  maxWidth: mode === "day" ? 44 : 56,
                  background: bg,
                  height: h,
                  borderRadius: "3px 3px 0 0",
                  outline: isToday ? `2px solid ${C.ink}` : "none",
                  outlineOffset: -2,
                }}
              />
            )}
            <span
              style={{
                fontFamily: MONO,
                fontSize: 11,
                color: isToday || b.is_mtd ? C.ink : C.z400,
                fontWeight: isToday || b.is_mtd ? 600 : 400,
              }}
            >
              {mode === "day" ? b.day : b.code}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ── Section wrapper ──────────────────────────────────────────────── */
const Section = ({ children, style }) => (
  <div style={{ marginBottom: 20, ...style }}>{children}</div>
);
const SecLabel = ({ children, extra, style }) => (
  <div
    style={{
      fontSize: 11.5,
      color: C.z500,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 14,
      ...style,
    }}
  >
    {children}
    {extra && <span>{extra}</span>}
  </div>
);

/* ── Page ─────────────────────────────────────────────────────────── */
export default function Pulse() {
  const [monthOpts, setMonthOpts] = useState(() => [monthKeyToOption(currentMonthKey())]);
  const [month, setMonth] = useState(null);
  const [dimMode, setDimMode] = useState("brand"); // brand | model | vertical | branch | sku
  // Feb 2026 — brand type-ahead filter on the SKU cut. Empty string
  // = no filter; a substring match on the SKU row's `brand` field.
  // Grand-total row + off-forecast rollup on this page still read
  // the full agg, so this is a client-side view filter only.
  const [skuBrandFilter, setSkuBrandFilter] = useState("");
  // Feb 2026 — page-wide Q/R toggle, persisted via localStorage.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("pulseViewMode") || "qty"; } catch { return "qty"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pulseViewMode", viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  const isRev = viewMode === "rev";
  const [chartMode, setChartMode] = useState("day"); // day | week
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [poolOpen, setPoolOpen] = useState(false);
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const userRoles = useAuthStore((s) => s.userRoles);
  // Aug 2026 — SKU funnel Excel export button is MASTER_ADMIN only.
  // Mirrors the backend guard on `GET /pulse/funnel/sku-export`.
  const isMasterAdmin = user?.role === "master_admin" || (userRoles || []).includes("MASTER_ADMIN");

  const load = useCallback(
    async (mk, signal) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/pulse/funnel?month=${mk}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal,
        });
        return res.data;
      } catch (e) {
        if (axios.isCancel?.(e) || e?.name === "CanceledError") return null;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "";
        const isPermissionDenied = status === 403 && detail.toLowerCase().includes("master_admin");
        if (isPermissionDenied) {
          navigate("/dashboard", { replace: true });
          return null;
        }
        setError({ status, message: detail || e?.message || "Failed to load Pulse" });
        toast.error(detail || "Failed to load Pulse");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token, navigate],
  );

  useEffect(() => {
    const c = new AbortController();
    if (!month) return () => c.abort();
    load(month, c.signal).then((d) => d && setData(d));
    return () => c.abort();
  }, [load, month]);

  // Fetch the data-driven month directory once on mount. Falls back
  // to a single current-month toggle if the endpoint fails so the
  // page still renders.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/pulse/available-months`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        const months = Array.isArray(res.data?.months) ? res.data.months : [];
        const current = res.data?.current_month || currentMonthKey();
        const opts = (months.length ? months : [current]).map(monthKeyToOption);
        setMonthOpts(opts);
        setMonth((prev) => prev || current);
      } catch (e) {
        if (cancelled) return;
        const fallback = currentMonthKey();
        setMonthOpts([monthKeyToOption(fallback)]);
        setMonth((prev) => prev || fallback);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const doRefresh = async () => {
    if (!month) return;
    setRefreshing(true);
    try {
      await axios.post(
        `${API}/pulse/refresh?month=${month}&scope=all`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success("Pulse refreshed");
      const d = await load(month);
      if (d) setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 10, flexWrap: "wrap", gap: 12 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.3px", margin: 0 }} data-testid="pulse-title">
          Company Pulse
        </h1>
        <div style={{ fontSize: 13, color: C.z500, marginTop: 4 }} data-testid="pulse-subtitle">
          {month
            ? <>Demand → dispatch funnel · all sites · {MONTH_LABELS[parseInt(month.slice(5, 7), 10) - 1]} {month.slice(0, 4)}</>
            : <>Demand → dispatch funnel · all sites</>}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ fontSize: 12, color: C.z400 }} data-testid="pulse-sync-info">
          {data?.jobA_run_at || data?.jobB_run_at ? (
            <>
              <span data-testid="pulse-cadence-caption">as of today 06:00 IST</span>
              {" · "}Last plan{" "}
              {data?.jobA_run_at
                ? new Date(data.jobA_run_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })
                : "—"}
              {" · "}Last actuals{" "}
              {data?.jobB_run_at
                ? new Date(data.jobB_run_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })
                : "—"}
            </>
          ) : data?.updated_at
            ? `Synced ${new Date(data.updated_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}`
            : "Not yet synced"}
        </span>
        <Button variant="outline" size="sm" onClick={doRefresh} disabled={refreshing} data-testid="pulse-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        {/* Feb 2026 — Q/₹ toggle, orange active state (accent). */}
        <div
          style={{
            display: "inline-flex", borderRadius: 8,
            border: `1px solid ${C.z200}`, overflow: "hidden", background: "#fff",
          }}
          data-testid="pulse-view-toggle"
        >
          {[{ key: "qty", label: "Quantity" }, { key: "rev", label: "Revenue ₹" }].map((o) => {
            const active = viewMode === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => setViewMode(o.key)}
                data-testid={`pulse-view-${o.key}`}
                style={{
                  padding: "6px 14px", fontSize: 12, fontWeight: 500,
                  background: active ? C.orange : "#fff",
                  color: active ? "#fff" : C.z500,
                  border: "none", cursor: "pointer",
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <Toggle
          options={monthOpts}
          value={month}
          onChange={setMonth}
          testIdPrefix="pulse-month"
        />
      </div>
    </div>
  );

  if (loading && !data) {
    return (
      <div style={{ padding: 32, maxWidth: 1400, margin: "0 auto" }} data-testid="pulse-loading">
        {header}
        <div style={{ height: 200, background: C.z100, borderRadius: 8, marginTop: 24, animation: "pulse 2s infinite" }} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ padding: 32, maxWidth: 1400, margin: "0 auto" }} data-testid="pulse-error">
        {header}
        <div
          style={{
            marginTop: 24, padding: 24, border: `1px solid ${C.z200}`, borderRadius: 8,
            display: "flex", flexDirection: "column", gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.red }}>
            <AlertCircle className="w-5 h-5" />
            <div style={{ fontWeight: 500 }}>Couldn&apos;t load Company Pulse</div>
          </div>
          <div style={{ fontSize: 13, color: C.z500 }} data-testid="pulse-error-detail">
            {error.message}
            {error.status ? ` (HTTP ${error.status})` : ""}
          </div>
          <Button onClick={() => load(month).then((d) => d && setData(d))} variant="outline" size="sm" data-testid="pulse-retry-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data || data.empty || (data.aggregates && data.aggregates.forecast_sku_count === 0)) {
    const monLabel = month
      ? `${MONTH_LABELS[parseInt(month.slice(5, 7), 10) - 1]} ${month.slice(0, 4)}`
      : "";
    const syncStamp = data?.updated_at
      ? new Date(data.updated_at).toLocaleString("en-IN", {
          hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short",
        })
      : "not yet synced";
    return (
      <div style={{ padding: 32, maxWidth: 1400, margin: "0 auto" }} data-testid="pulse-empty">
        {header}
        <Legend revenue={isRev} />
        <div
          style={{
            padding: 60, textAlign: "center", border: `1px dashed ${C.z200}`,
            borderRadius: 8, background: C.z100,
          }}
          data-testid="pulse-empty-msg"
        >
          <div style={{ fontSize: 15, fontWeight: 500, color: C.ink, marginBottom: 6 }}>
            No forecast published for {monLabel}
          </div>
          <div style={{ fontSize: 12.5, color: C.z500 }}>
            The funnel renders once at least one buyer SKU has a forecast for this month.
            <br />
            Synced {syncStamp}. Hit <b>Refresh</b> after publishing forecasts to rebuild the snapshot.
          </div>
        </div>
      </div>
    );
  }

  const agg = data.aggregates || {};
  // Feb 2026 — revenue view. When `isRev`, every card / joint /
  // leakage / summary reads from `agg.revenue.*` and formats as ₹Cr.
  // UNPRICED SKUs are already excluded from every ratio's numerator
  // and denominator by the backend (`_compute_aggregates`).
  const rev = agg.revenue || {};
  const conv = isRev ? (rev.conversion || {}) : (agg.conversion || {});
  const leak = isRev ? (rev.leakage || {}) : (agg.leakage || {});
  const unpriced = agg.unpriced || { sku_count: 0, forecast_qty: 0, rows: [] };
  const F_val = isRev ? rev.forecast_rev : agg.forecast_units;
  const I_val = isRev ? rev.opening_inventory_rev : agg.opening_inventory_units;
  const S_val = isRev ? rev.scheduled_rev : agg.scheduled_units;
  const P_val = isRev ? rev.produced_rev : agg.produced_units;
  const RDY_val = isRev ? rev.ready_rev : agg.ready_units;
  const Await_val = isRev ? rev.awaiting_rev : agg.awaiting_units;
  // Feb 2026 (Ready 3-way fork) — new Excess build branch.
  const Excess_val = isRev
    ? (rev.excess_build_rev || 0)
    : (agg.excess_build_units || 0);
  const Supply_val = isRev ? rev.supply_rev : agg.supply_units;
  const D_val = isRev ? rev.dispatched_rev : agg.dispatched_units;
  const dof = isRev ? rev.d_over_f_rev_pct : agg.d_over_f_pct;
  const fmtVal = isRev ? fmtCr : fmt;
  const rowsByDim = {
    brand: data.per_brand, vertical: data.per_vertical,
    model: data.per_model || [], branch: data.per_branch,
    sku: data.per_sku,
  };
  const rows = rowsByDim[dimMode] || [];
  // Apply the SKU brand type-ahead filter on the SKU cut only.
  const brandQ = (skuBrandFilter || "").trim().toLowerCase();
  const filteredRows = (dimMode === "sku" && brandQ)
    ? rows.filter((r) => ((r.brand || "").toString().toLowerCase().includes(brandQ)))
    : rows;
  // Distinct brand list from the current per_sku rows — feeds the
  // type-ahead datalist. Plain compute (not useMemo) — an early
  // return upstream would violate rules-of-hooks.
  const skuBrandOptions = (dimMode === "sku")
    ? [...new Set((rows || []).map((r) => r.brand).filter(Boolean).map(String))].sort(
        (a, b) => a.localeCompare(b),
      )
    : [];
  const todayISO = new Date().toISOString().slice(0, 10);
  const dailyBars = data.daily_dispatch || [];
  const todayBar = dailyBars.find((b) => b.date === todayISO);
  const todayUnits = todayBar ? (isRev ? todayBar.revenue : todayBar.units) : 0;

  return (
    <div style={{ padding: 32, maxWidth: 1400, margin: "0 auto", background: "#fff", color: C.ink }} data-testid="pulse-root">
      {header}
      <Legend revenue={isRev} />

      {/* Feb 2026 — Unpriced callout (revenue mode only). */}
      {isRev && unpriced.sku_count > 0 && (
        <div
          data-testid="pulse-unpriced-callout"
          style={{
            padding: "14px 18px", marginBottom: 20, borderRadius: 8,
            background: "#fef3c7", border: `1px solid ${C.amber}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            fontSize: 13, color: C.ink,
          }}
        >
          <div>
            <b style={{ color: C.amber }}>{unpriced.sku_count}</b> forecast SKUs are unpriced —{" "}
            <b style={{ fontFamily: MONO }}>{fmt(unpriced.forecast_qty)}</b> units excluded from the revenue funnel.
            {unpriced.dispatched_rev > 0 && (
              <> · <b style={{ fontFamily: MONO }}>{fmtCr(unpriced.dispatched_rev)}</b> dispatched (in totals, not in conversion %)</>
            )}
          </div>
          <button
            onClick={() => navigate("/price-master")}
            style={{
              background: "#fff", border: `1px solid ${C.amber}`, color: C.amber,
              padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500,
              cursor: "pointer",
            }}
            data-testid="pulse-unpriced-setprice-link"
          >
            Set price →
          </button>
        </div>
      )}

      {/* ── FUNNEL — Ready → (Dispatched, Awaiting) fork ── */}
      <Section style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr auto 1fr auto 1fr",
            gap: 0,
            alignItems: "start",
          }}
          data-testid="pulse-funnel"
        >
          <FunnelBox
            label="Forecast"
            code="F"
            value={isRev ? fmtCr(F_val) : F_val}
            valueRaw={isRev}
            subRight={`${agg.forecast_sku_count || 0} SKUs${isRev && agg.est_sku_count ? ` · ${agg.est_sku_count} est` : ""}`}
            testId="pulse-funnel-forecast"
            gmLine={isRev && rev.forecast_gm != null ? { value: fmtCr(rev.forecast_gm), pct: rev.forecast_gm_pct } : null}
          />
          <Joint pct={conv.f_to_supply_pct} delta={leak.f_to_supply} deltaFmt={isRev ? fmtCr : fmt} />
          <FunnelBox
            label="Supply plan"
            code="I+S"
            value={isRev ? fmtCr(Supply_val) : Supply_val}
            valueRaw={isRev}
            subLeft={`I ${fmtVal(I_val)}`}
            subRight={`S ${fmtVal(S_val)}`}
            testId="pulse-funnel-supply"
            gmLine={isRev && rev.supply_gm != null ? { value: fmtCr(rev.supply_gm), pct: rev.supply_gm_pct } : null}
          />
          <Joint pct={conv.supply_to_rdy_pct} delta={leak.supply_to_rdy} deltaFmt={isRev ? fmtCr : fmt} />
          <FunnelBox
            label="Ready"
            code="I+P"
            value={isRev ? fmtCr(RDY_val) : RDY_val}
            valueRaw={isRev}
            subLeft={`I ${fmtVal(I_val)}`}
            subRight={`P ${fmtVal(P_val)}`}
            testId="pulse-funnel-ready"
            pivot
            badge="— view pool ↗"
            onClick={() => setPoolOpen(true)}
            gmLine={isRev && rev.ready_gm != null ? { value: fmtCr(rev.ready_gm), pct: rev.ready_gm_pct } : null}
          />
          {/* Aug 2026 layout spec — drawn branch connector (SVG
              elbow) from Ready → the stacked outcome cards. Renders
              as a right-then-down path through the empty grid cell. */}
          <div style={{ position: "relative", width: 44, minHeight: 140 }} data-testid="pulse-funnel-connector">
            <svg width="44" height="140" viewBox="0 0 44 140" style={{ position: "absolute", inset: 0 }}>
              <path d="M0 70 L22 70 L22 24 L44 24" stroke={C.z300} strokeWidth="1" fill="none" />
              <path d="M0 70 L22 70 L22 116 L44 116" stroke={C.z300} strokeWidth="1" fill="none" />
              <polygon points="44,24 38,21 38,27" fill={C.z300} />
              <polygon points="44,116 38,113 38,119" fill={C.z300} />
            </svg>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="pulse-funnel-ready-fork">
            <FunnelBox
              label="Dispatched"
              code="D"
              value={isRev ? fmtCr(D_val) : D_val}
              valueRaw={isRev}
              valueColor={C.orange}
              accent={C.orange}
              subLeft={`${(() => {
                const pct = RDY_val > 0 ? Math.round((D_val / RDY_val) * 1000) / 10 : 0;
                return `${pct}% of ready`;
              })()}`}
              subRight={`${fmtPct(dof)} of forecast`}
              footnote={
                isRev && unpriced.dispatched_rev > 0
                  ? `+${fmtCr(unpriced.dispatched_rev)} on ${unpriced.sku_count} unpriced SKUs, excluded from funnel conversion`
                  : null
              }
              testId="pulse-funnel-dispatched"
              gmLine={isRev && rev.dispatched_gm != null ? { value: fmtCr(rev.dispatched_gm), pct: rev.dispatched_gm_pct } : null}
            />
            <FunnelBox
              label="Awaiting dispatch"
              code="min(F,RDY)−D"
              value={isRev ? fmtCr(Await_val) : Await_val}
              valueRaw={isRev}
              accent={C.z400}
              subLeft={`${(() => {
                const pct = RDY_val > 0 ? Math.round((Await_val / RDY_val) * 1000) / 10 : 0;
                return `${pct}%`;
              })()}`}
              subRight={"bounded by forecast"}
              testId="pulse-funnel-awaiting"
              badge="— view pool ↗"
              onClick={() => setPoolOpen(true)}
            />
            {/* Feb 2026 (Ready 3-way fork) — Excess build. Production
                above forecast lands here instead of inflating Awaiting. */}
            <FunnelBox
              label="Excess build"
              code="RDY−max(F,D)"
              value={isRev ? fmtCr(Excess_val) : Excess_val}
              valueRaw={isRev}
              accent={C.amber || "#d97706"}
              subLeft={`${(() => {
                const pct = RDY_val > 0 ? Math.round((Excess_val / RDY_val) * 1000) / 10 : 0;
                return `${pct}% of ready`;
              })()}`}
              subRight={"over forecast"}
              testId="pulse-funnel-excess-build"
            />
          </div>
        </div>
        {/* Reconciliation line — the fork's arithmetic invariant.
            Feb 2026 — in revenue mode fmtCr rounds each segment
            independently, which can leave a 1-paise gap that reads
            as a broken sum. Absorb the rounding remainder into the
            Excess build segment so the displayed sum always equals
            the displayed Ready. */}
        <div
          style={{
            fontSize: 12, color: C.z500, marginTop: 12, textAlign: "right",
            fontFamily: MONO,
          }}
          data-testid="pulse-funnel-reconcile"
        >
          {isRev ? (() => {
            const roundCr = (v) => Math.round((Number(v) || 0) / 1e5) / 100;
            const dRounded = roundCr(D_val);
            const aRounded = roundCr(Await_val);
            const rdyRounded = roundCr(RDY_val);
            const eAbsorbed = Math.round((rdyRounded - dRounded - aRounded) * 100) / 100;
            const fmtCrExact = (v) => `₹${v.toFixed(2)} Cr`;
            return (
              <>Dispatched {fmtCrExact(dRounded)} + Awaiting {fmtCrExact(aRounded)} + Excess build {fmtCrExact(eAbsorbed)} = Ready {fmtCrExact(rdyRounded)}</>
            );
          })() : (
            <>Dispatched {fmtVal(D_val)} + Awaiting {fmtVal(Await_val)} + Excess build {fmtVal(Excess_val)} = Ready {fmtVal(RDY_val)}</>
          )}
        </div>
        {/* Feb 2026 — GM per stage note. Rev mode only. Names the
            count of SKUs excluded from GM because they lack a
            reliable BOM cost (missing RM price in the roll-up) or
            a price (UNPRICED price_basis). Mirrors the existing
            unpriced callout tone (small, muted). */}
        {isRev && rev.gm_note && rev.gm_note.excluded_sku_count > 0 && (
          <div
            style={{
              fontSize: 11.5, color: C.z500, marginTop: 6, textAlign: "right",
              fontFamily: MONO,
            }}
            data-testid="pulse-funnel-gm-note"
          >
            {rev.gm_note.excluded_sku_count} SKUs excluded from GM
            {" ("}
            {rev.gm_note.excluded_reasons.no_cost > 0
              ? `${rev.gm_note.excluded_reasons.no_cost} no reliable BOM cost`
              : null}
            {rev.gm_note.excluded_reasons.no_cost > 0
              && rev.gm_note.excluded_reasons.no_price > 0
              && ", "}
            {rev.gm_note.excluded_reasons.no_price > 0
              ? `${rev.gm_note.excluded_reasons.no_price} no price`
              : null}
            {")"}
          </div>
        )}
        {/* Feb 2026 — headline reconciliation. The fork's Dispatched
            value (`D_forecast`) is by design forecast-only. This
            second line explains where the day/week charts' larger
            total comes from and links the two numbers.
            Aug 2026 (label-rounding bug fix) — the components MUST
            sum to the displayed total. Formatters (`fmtCr .toFixed(2)`
            / `fmt` .round) can drop ±0.01 between the components and
            the total when computed independently; the fix rounds
            components first, then derives the displayed total as
            their sum. Backing values still logged via testids for
            reconciliation audits. */}
        {(() => {
          const forecastQty = agg.D_forecast || 0;
          const offQty = agg.D_offforecast || 0;
          // `rev.dispatched_rev` is FORECAST-only revenue INCLUDING
          // unpriced (backend line 1334: `tot_D_rev` sums over
          // `per_sku_rows` which is the priced ∪ unpriced forecast
          // universe). `dispatched_rev_priced` is a different metric
          // used by conversion / price-realisation ONLY — do NOT bind
          // it here or the reconciliation drops the unpriced bucket.
          const forecastRev = (rev && rev.dispatched_rev) || 0;
          const offRev = agg.D_offforecast_rev || 0;
          if (!offQty && !offRev) return null;  // clean month → skip
          const skuCount = agg.D_offforecast_sku_count || 0;

          // ── Rounding-safe display ──────────────────────────────
          // Rev mode: round each component to the same precision the
          // label renders (2 dp in Cr = 1e5). Sum the rounded
          // components for the total so the line always foots.
          // Qty mode: round each component to the same precision the
          // label renders (integer units). Same rule.
          const roundToDisplay = (v) => {
            if (isRev) {
              const cr = v / 1e7;
              return Math.round(cr * 100) / 100 * 1e7;  // → nearest lakh
            }
            return Math.round(v);
          };
          const forecastDisp = roundToDisplay(isRev ? forecastRev : forecastQty);
          const offDisp = roundToDisplay(isRev ? offRev : offQty);
          const totalDisp = forecastDisp + offDisp;  // sum of rounded parts

          return (
            <div
              style={{ fontSize: 11.5, color: C.z400, marginTop: 6,
                       textAlign: "right", fontFamily: MONO }}
              data-testid="pulse-funnel-reconcile-total"
              data-forecast-raw={String(isRev ? forecastRev : forecastQty)}
              data-off-raw={String(isRev ? offRev : offQty)}
            >
              Shipped total = <span data-testid="pulse-funnel-reconcile-forecast">{fmtVal(forecastDisp)}</span> forecast +{" "}
              <span data-testid="pulse-funnel-reconcile-off">{fmtVal(offDisp)}</span> off-forecast{skuCount ? ` (${skuCount} SKUs)` : ""} ={" "}
              <span data-testid="pulse-funnel-reconcile-sum">{fmtVal(totalDisp)}</span>
            </div>
          );
        })()}
      </Section>

      {/* ── SUMMARY LINE ── */}
      <div
        style={{
          fontSize: 13, color: C.z500, padding: "14px 0",
          borderTop: `1px solid ${C.z150}`, marginBottom: 40,
        }}
        data-testid="pulse-summary-line"
      >
        End-to-end{" "}
        <b style={{ fontFamily: MONO, color: C.ink }}>{fmtPct(dof)}</b> of forecast dispatched
        <span style={{ color: C.z300, margin: "0 14px" }}>·</span>
        total leakage{" "}
        <b style={{ fontFamily: MONO, color: C.red }}>{fmtVal(leak.total)}</b>{isRev ? "" : " units"}
        {isRev && rev.price_realisation_pct !== undefined && rev.dispatched_at_plan > 0 && (
          <>
            <span style={{ color: C.z300, margin: "0 14px" }}>·</span>
            price realisation{" "}
            <b style={{ fontFamily: MONO, color: rev.price_realisation_pct < 100 ? C.red : C.green }} data-testid="pulse-price-realisation-pct">
              {fmtPct(rev.price_realisation_pct)}
            </b>{" "}
            <span style={{ fontSize: 11.5, color: C.z400 }} data-testid="pulse-price-realisation-detail">
              (dispatched at {fmtCr(rev.dispatched_rev_priced)} vs {fmtCr(rev.dispatched_at_plan)} at plan)
            </span>
          </>
        )}
        {!isRev && (
          <>
            <span style={{ color: C.z300, margin: "0 14px" }}>·</span>
            <b style={{ fontFamily: MONO, color: C.ink }}>{agg.oversold_sku_count || 0}</b> SKUs over-sold, max D/F{" "}
            <b style={{ fontFamily: MONO, color: C.ink }}>{fmtPct(agg.max_d_over_f_pct)}</b>
          </>
        )}
      </div>

      {/* ── LEAKAGE ── */}
      <Section>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64 }}>
          <div>
            <SecLabel>
              Leakage · F → supply → RDY{" "}
              <span style={{ fontFamily: MONO, color: C.red, textTransform: "none", letterSpacing: 0, marginLeft: 8 }}>
                −{fmt((leak.f_to_supply || 0) + (leak.supply_to_rdy || 0))}
              </span>
            </SecLabel>
            <KV k="Net requirement never scheduled" v={leak.f_to_supply} color={C.red} data-testid="pulse-leak-f-supply" />
            <KV k="Shortfall — scheduled but not produced" v={leak.supply_to_rdy} color={C.red} data-testid="pulse-leak-supply-rdy" />
            <KV k="Month-opening carried into RDY" v={agg.opening_inventory_units} color={C.z500} />
            <KV k="Produced this month" v={agg.produced_units} color={C.z500} data-testid="pulse-produced-this-month" />
          </div>
          <div>
            <SecLabel>
              Leakage · RDY → D{" "}
              <span style={{ fontFamily: MONO, color: C.red, textTransform: "none", letterSpacing: 0, marginLeft: 8 }}>
                −{fmt(leak.rdy_to_d)}
              </span>
            </SecLabel>
            <KV k="Sitting ready, no dispatch" v={leak.rdy_to_d} color={C.red} data-testid="pulse-leak-rdy-d" />
            <KV k="Oversold SKUs (D > F)" v={agg.oversold_sku_count} color={agg.oversold_sku_count ? C.orange : C.z500} />
            <KV k="Max D/F observed" v={fmtPct(agg.max_d_over_f_pct)} color={C.z500} raw />
            <KV k="Inventory basis" v="Month-opening frozen · P daily" color={C.z500} raw />
          </div>
        </div>
      </Section>

      {/* ── DISPATCHES + TREND ── */}
      <Section>
        <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 64 }}>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <SecLabel style={{ margin: 0 }}>Dispatches</SecLabel>
              <Toggle
                options={[{ key: "day", label: "Day" }, { key: "week", label: "Week" }]}
                value={chartMode}
                onChange={setChartMode}
                testIdPrefix="pulse-chart-mode"
              />
            </div>
            {chartMode === "day" ? (
              <>
                <div style={{ fontSize: 12.5, color: C.z500, marginBottom: 14 }}>
                  Last 14 days — today{" "}
                  <b style={{ fontFamily: MONO, color: C.orange, fontSize: 15 }} data-testid="pulse-today-units">
                    {isRev ? fmtCr(todayUnits) : fmt(todayUnits)}
                  </b>{" "}
                  {isRev ? "revenue" : "units"}
                </div>
                <BarChart bars={dailyBars} mode="day" todayISO={todayISO} valueKey={isRev ? "revenue" : "units"} />
                <div style={{ fontSize: 11.5, color: C.z400, marginTop: 10 }}>
                  Gray = prior month · orange = current month · outlined = today
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12.5, color: C.z500, marginBottom: 14 }}>
                  ISO week — actual dispatched{isRev ? " ₹" : ""} · MTD week partial
                </div>
                <BarChart bars={data.weekly_dispatch || []} mode="week" valueKey={isRev ? "revenue" : "units"} />
                <div style={{ fontSize: 11.5, color: C.z400, marginTop: 10 }}>
                  Future weeks fill in as they happen
                </div>
              </>
            )}
          </div>

          <div>
            <SecLabel>Conversion trend</SecLabel>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.z200}` }}>
                  <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 500, color: C.z500 }}>Month</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 500, color: C.z500 }}>F→I+S</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 500, color: C.z500 }}>I+S→RDY</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 500, color: C.z500 }}>RDY→D</th>
                  <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 500, color: C.z500 }}>D/F</th>
                </tr>
              </thead>
              <tbody data-testid="pulse-trend-table">
                {(data.trend || []).map((t) => (
                  <tr key={t.month_key} style={{ borderBottom: `1px solid ${C.z150}` }}>
                    <td style={{ padding: "8px 0", color: t.is_mtd ? C.orange : C.z500, fontWeight: t.is_mtd ? 600 : 400 }}>
                      {MONTH_LABELS[parseInt(t.month_key.slice(5, 7), 10) - 1]}
                      {t.is_mtd ? " MTD" : ""}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO }}>{fmtPct(isRev ? t.f_to_supply_rev_pct : t.f_to_supply_pct)}</td>
                    <td
                      style={{
                        textAlign: "right",
                        padding: "8px",
                        fontFamily: MONO,
                        color: (isRev ? t.supply_to_rdy_rev_pct : t.supply_to_rdy_pct) < 80 ? C.amber : undefined,
                      }}
                    >
                      {fmtPct(isRev ? t.supply_to_rdy_rev_pct : t.supply_to_rdy_pct)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        padding: "8px",
                        fontFamily: MONO,
                        color: (isRev ? t.rdy_to_d_rev_pct : t.rdy_to_d_pct) < 85 ? C.amber : undefined,
                      }}
                    >
                      {fmtPct(isRev ? t.rdy_to_d_rev_pct : t.rdy_to_d_pct)}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px 0", fontFamily: MONO, fontWeight: 600 }}>
                      {fmtPct(isRev ? t.d_over_f_rev_pct : t.d_over_f_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* ── FUNNEL BY DIMENSION ── */}
      <Section>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <SecLabel style={{ margin: 0 }}>Funnel by {dimMode}</SecLabel>
          <Toggle
            options={[
              { key: "brand", label: "Brand" },
              { key: "model", label: "Model" },
              { key: "vertical", label: "Vertical" },
              { key: "branch", label: "Branch" },
              { key: "sku", label: "SKU" },
            ]}
            value={dimMode}
            onChange={(k) => { setDimMode(k); if (k !== "sku") setSkuBrandFilter(""); }}
            testIdPrefix="pulse-dim"
          />
        </div>
        {dimMode === "sku" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <input
              type="text"
              list="pulse-sku-brand-options"
              placeholder="Filter by brand…"
              value={skuBrandFilter}
              onChange={(e) => setSkuBrandFilter(e.target.value)}
              data-testid="pulse-sku-brand-filter"
              style={{
                padding: "6px 10px", fontSize: 13, borderRadius: 6,
                border: `1px solid ${C.z300}`, minWidth: 220, fontFamily: MONO,
                outline: "none",
              }}
            />
            <datalist id="pulse-sku-brand-options">
              {skuBrandOptions.map((b) => <option key={b} value={b} />)}
            </datalist>
            {skuBrandFilter && (
              <button
                onClick={() => setSkuBrandFilter("")}
                data-testid="pulse-sku-brand-filter-clear"
                style={{
                  padding: "4px 10px", fontSize: 12, borderRadius: 6,
                  border: `1px solid ${C.z300}`, background: "#fff", cursor: "pointer",
                }}
              >Clear</button>
            )}
            <span style={{ fontSize: 12, color: C.z500, fontFamily: MONO }}>
              {filteredRows.length} of {rows.length} SKUs
            </span>
            {/* Aug 2026 — SKU-cut Excel export (full-spectrum: forecast
                ∪ off-forecast ∪ plan-only). MASTER_ADMIN only; button
                is hidden for other roles (backend also enforces).
                Mode + active brand filter are threaded through. */}
            {isMasterAdmin && (
              <button
                onClick={async () => {
                  try {
                    const params = new URLSearchParams({
                      month: month,
                      mode: isRev ? "revenue" : "quantity",
                    });
                    if ((skuBrandFilter || "").trim()) {
                      params.set("brand", skuBrandFilter.trim());
                    }
                    const res = await axios.get(
                      `${API}/pulse/funnel/sku-export?${params.toString()}`,
                      {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                        responseType: "blob",
                      },
                    );
                    // Derive filename from Content-Disposition when present.
                    const cd = res.headers?.["content-disposition"] || "";
                    const match = cd.match(/filename="?([^";]+)"?/i);
                    const fname = match
                      ? match[1]
                      : `pulse_sku_funnel_${month}_${isRev ? "revenue" : "quantity"}.xlsx`;
                    const url = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a");
                    a.href = url; a.download = fname;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error("SKU export failed", e);
                  }
                }}
                data-testid="pulse-sku-export-btn"
                style={{
                  marginLeft: "auto",
                  padding: "5px 12px", fontSize: 12, borderRadius: 6,
                  border: `1px solid ${C.z300}`, background: "#fff", cursor: "pointer",
                  fontFamily: MONO,
                }}
                title="Download the full-spectrum SKU funnel (forecast ∪ plan-only ∪ off-forecast) as Excel"
              >
                ↓ Download Excel
              </button>
            )}
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="pulse-dim-table">
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.z200}` }}>
              <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 500, color: C.z500 }}>
                {dimMode === "sku" ? "SKU" : dimMode[0].toUpperCase() + dimMode.slice(1)}
              </th>
              {(isRev
                ? ["F ₹", "Supply ₹", "Ready ₹", "Dispatched ₹", "Awaiting ₹", "Excess build ₹", "D/F ₹", "ASP plan → actual", "GM %"]
                : ["F", "I", "S", "Ready", "Dispatched", "Awaiting", "Excess build", "D/F"]
              ).map((h, hi) => {
                // Aug 2026 layout spec — group dividers before I,
                // before Ready, before Dispatched, before D/F to
                // visually group Supply / Available / Outcome. Feb
                // 2026 — index shifted by +1 after inserting the
                // Excess build column between Awaiting and D/F.
                const dividerBefore = isRev
                  ? new Set([0, 2, 3, 6, 8]).has(hi)   // rev headers: F ₹|Supply ₹|Ready ₹|Dispatched ₹|Awaiting ₹|Excess ₹|D/F ₹|ASP|GM
                  : new Set([1, 3, 4, 7]).has(hi); // qty headers: F|I|S|Ready|Dispatched|Awaiting|Excess|D/F
                return (
                  <th
                    key={h}
                    style={{
                      textAlign: "right", padding: "6px 8px", fontWeight: 500, color: C.z500,
                      borderLeft: dividerBefore ? `1px solid ${C.z200}` : "none",
                    }}
                  >{h}</th>
                );
              })}
              <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 500, color: C.z500 }}>Biggest leak</th>
            </tr>
          </thead>
          <tbody>
            {(filteredRows || [])
              .filter((r) => !isRev || (r.price_basis || "MASTER") !== "UNPRICED")
              .slice(0, 30)
              .map((r, i) => {
              const dofRow = isRev ? (r.d_over_f_rev_pct ?? 0) : (r.d_over_f_pct || 0);
              const dofColor = dofRow < 60 ? C.red : dofRow < 80 ? C.amber : dofRow > 120 ? C.orange : C.ink;
              const leakColor = r.biggest_leak_stage === "Excess build" ? C.orange : dofRow < 60 ? C.red : C.amber;
              const isEst = (r.price_basis === "EST") || (r.est_count > 0);
              return (
                <tr key={r.name || r.sku_id || i} style={{ borderBottom: `1px solid ${C.z150}` }} data-testid={`pulse-dim-row-${i}`}>
                  <td style={{ padding: "8px 0", fontWeight: 600 }}>
                    {dimMode === "sku" ? (
                      <>
                        <div>
                          {r.sku_id}
                          {isRev && isEst && (
                            <span
                              data-testid={`pulse-dim-est-${i}`}
                              style={{
                                marginLeft: 6, fontSize: 10, padding: "1px 5px", borderRadius: 3,
                                background: "#fef3c7", color: C.amber, fontFamily: MONO, fontWeight: 600,
                              }}
                            >est</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: C.z400, fontWeight: 400 }}>{r.description}</div>
                      </>
                    ) : (
                      <>
                        {r.name}
                        {isRev && isEst && (
                          <span
                            data-testid={`pulse-dim-est-${i}`}
                            style={{
                              marginLeft: 6, fontSize: 10, padding: "1px 5px", borderRadius: 3,
                              background: "#fef3c7", color: C.amber, fontFamily: MONO, fontWeight: 600,
                            }}
                          >est</span>
                        )}
                      </>
                    )}
                  </td>
                  {isRev ? (
                    <>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO }}>{fmtCr(r.F_rev)}</td>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, color: C.blue }}>{fmtCr(r.supply_rev)}</td>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO }}>{fmtCr(r.ready_rev)}</td>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, fontWeight: 600, color: C.orange }}>{fmtCr(r.D_rev)}</td>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO }}>{fmtCr(r.awaiting_rev || 0)}</td>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, color: C.amber || "#d97706" }} data-testid={`pulse-dim-excess-${i}`}>{fmtCr(r.excess_build_rev || 0)}</td>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, fontWeight: 600, color: dofColor }}>{fmtPct(dofRow)}</td>
                      <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, fontSize: 12, color: C.z500 }}>
                        {fmtRupees(r.asp_plan ?? r.blended_plan_price)}{" → "}
                        <span style={{ color: (r.asp_actual || 0) < (r.asp_plan || r.blended_plan_price || 0) ? C.red : C.green }}>
                          {fmtRupees(r.asp_actual)}
                        </span>
                      </td>
                      {/* Feb 2026 — GM %. Dispatched GM % primary,
                          falls back to Forecast GM % when the row has
                          D=0 this month. Blank when neither leg has a
                          reliable computation for this rollup. */}
                      {(() => {
                        const hasDispatchGm = (r.D || 0) > 0 && r.D_gm_pct != null && r.D_gm_pct !== 0;
                        const gmPct = hasDispatchGm ? r.D_gm_pct : r.F_gm_pct;
                        const gmSrc = hasDispatchGm ? "D" : "F";
                        const gmValid = gmPct != null && !(gmPct === 0 && (r.D_gm || 0) === 0 && (r.F_gm || 0) === 0);
                        const gmColor = !gmValid ? C.z400 : (gmPct < 0 ? C.red : gmPct < 10 ? C.amber : C.green);
                        return (
                          <td
                            style={{
                              textAlign: "right", padding: "8px", fontFamily: MONO,
                              fontWeight: 600, color: gmColor,
                              borderLeft: `1px solid ${C.z200}`,
                            }}
                            data-testid={`pulse-dim-gm-${i}`}
                            title={gmValid ? `${gmSrc === "D" ? "Dispatched" : "Forecast"} GM %` : "No reliable BOM cost or price"}
                          >
                            {gmValid ? `${gmPct.toFixed(1)}%` : "—"}
                          </td>
                        );
                      })()}
                    </>
                  ) : (() => {
                    // Aug 2026 — Branch cut blanks F + D/F (no branch-
                    // level forecast). Everything else renders.
                    const blankFandDoF = dimMode === "branch";
                    const dv = (v) => <>{v}</>;
                    return (
                      <>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO }}>{blankFandDoF ? "—" : fmt(r.F)}</td>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, color: C.green, borderLeft: `1px solid ${C.z200}` }}>{fmt(r.I)}</td>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO }}>{fmt(r.S)}</td>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, borderLeft: `1px solid ${C.z200}` }}>{fmt(r.ready)}</td>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, fontWeight: 600, color: C.orange, borderLeft: `1px solid ${C.z200}` }}>{dv(fmt(r.D))}</td>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO }}>{fmt(r.awaiting || 0)}</td>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, color: C.amber || "#d97706" }} data-testid={`pulse-dim-excess-${i}`}>{fmt(r.excess_build || 0)}</td>
                        <td style={{ textAlign: "right", padding: "8px", fontFamily: MONO, fontWeight: 600, color: dofColor, borderLeft: `1px solid ${C.z200}` }}>{blankFandDoF ? "—" : fmtPct(dofRow)}</td>
                      </>
                    );
                  })()}
                  <td style={{ textAlign: "right", padding: "8px 0", fontSize: 12, color: r.biggest_leak_stage ? leakColor : C.z400 }}>
                    {r.biggest_leak_stage || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Aug 2026 layout spec — grand-total row (bold, top-
              bordered). Column sums; Total D/F = ΣD/ΣF; Σ Dispatched
              + Σ Awaiting = Σ Ready (invariant). Branch cut blanks
              F and D/F.
              Feb 2026 — total now sums the FULL filtered population
              (not the truncated top-30) and respects the SKU brand
              filter so a filtered view's total = sum of the filtered
              rows only. */}
          {(() => {
            // Full population post-brand-filter, post-price-basis-filter.
            // The top-30 slice is presentational only; totals reconcile
            // against every row that would be visible if we didn't
            // truncate.
            const filtered = (filteredRows || []).filter((r) => !isRev || (r.price_basis || "MASTER") !== "UNPRICED");
            if (!filtered.length) return null;
            const sum = (k) => filtered.reduce((a, r) => a + (Number(r[k]) || 0), 0);
            const blankFandDoF = dimMode === "branch";
            const totF = sum("F"), totD = sum("D");
            const totDoF = totF > 0 ? (totD / totF) * 100 : 0;
            const dofColor = totDoF < 60 ? C.red : totDoF < 80 ? C.amber : totDoF > 120 ? C.orange : C.ink;
            const labelSuffix = (dimMode === "sku" && skuBrandFilter)
              ? ` — filtered · ${filtered.length}` : ` — all ${filtered.length}`;
            return (
              <tfoot data-testid="pulse-dim-total">
                <tr style={{ borderTop: `2px solid ${C.ink}`, fontWeight: 700 }}>
                  <td style={{ padding: "10px 0" }}>Total{labelSuffix}</td>
                  {isRev ? (
                    <>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO }}>{fmtCr(sum("F_rev"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: C.blue, borderLeft: `1px solid ${C.z200}` }}>{fmtCr(sum("supply_rev"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, borderLeft: `1px solid ${C.z200}` }}>{fmtCr(sum("ready_rev"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: C.orange, borderLeft: `1px solid ${C.z200}` }}>{fmtCr(sum("D_rev"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO }}>{fmtCr(sum("awaiting_rev"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: C.amber || "#d97706" }} data-testid="pulse-dim-total-excess">{fmtCr(sum("excess_build_rev"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: dofColor, borderLeft: `1px solid ${C.z200}` }}>{fmtPct(totDoF)}</td>
                      <td />
                    </>
                  ) : (
                    <>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO }}>{blankFandDoF ? "—" : fmt(totF)}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: C.green, borderLeft: `1px solid ${C.z200}` }}>{fmt(sum("I"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO }}>{fmt(sum("S"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, borderLeft: `1px solid ${C.z200}` }}>{fmt(sum("ready"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: C.orange, borderLeft: `1px solid ${C.z200}` }}>{fmt(totD)}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO }}>{fmt(sum("awaiting"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: C.amber || "#d97706" }} data-testid="pulse-dim-total-excess">{fmt(sum("excess_build"))}</td>
                      <td style={{ textAlign: "right", padding: "10px 8px", fontFamily: MONO, color: dofColor, borderLeft: `1px solid ${C.z200}` }}>{blankFandDoF ? "—" : fmtPct(totDoF)}</td>
                    </>
                  )}
                  <td />
                </tr>
              </tfoot>
            );
          })()}
        </table>
        <div style={{ fontSize: 11.5, color: C.z400, marginTop: 12 }}>
          Showing top 30 by forecast · click any {dimMode} row to drill deeper (SKU-level export coming soon)
        </div>
      </Section>

      {/* ── AWAITING POOL MODAL ── Aug 2026 layout spec — opens
          from the Ready pivot or the Awaiting card. Lists every
          forecast SKU with Awaiting > 0, sorted by Awaiting desc,
          showing the Ready/Dispatched/Awaiting split with I + P
          breakdown so ops can see WHERE the ready-to-ship pile came
          from. */}
      {poolOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60,
          }}
          data-testid="pulse-pool-modal"
          onClick={() => setPoolOpen(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: 12, maxWidth: 1100, width: "90%",
              maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column",
              border: `1px solid ${C.z200}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.z200}` }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: C.ink }}>Awaiting dispatch — ready to ship today</div>
              <div style={{ fontSize: 12, color: C.z500, marginTop: 4, fontFamily: MONO }}>
                On-hand now · {fmt(Await_val)} units ·{" "}
                {(rows || []).filter((r) => (Number(r.awaiting) || 0) > 0).length} SKUs · as of{" "}
                {data?.jobB_run_at ? new Date(data.jobB_run_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : "—"}
              </div>
            </div>
            <div style={{ overflow: "auto", padding: "0 22px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.z200}`, position: "sticky", top: 0, background: "#fff" }}>
                    {["SKU", "Brand", "Ready", "Dispatched", "Awaiting", "= I", "+ P", "Branch"].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          textAlign: i < 2 ? "left" : "right",
                          padding: "10px 8px", fontWeight: 500, color: C.z500,
                        }}
                      >{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data?.per_sku || [])
                    .filter((r) => (Number(r.awaiting) || 0) > 0)
                    .sort((a, b) => (Number(b.awaiting) || 0) - (Number(a.awaiting) || 0))
                    .slice(0, 200)
                    .map((r, i) => (
                      <tr key={r.sku_id || i} style={{ borderBottom: `1px solid ${C.z150}` }} data-testid={`pulse-pool-row-${i}`}>
                        <td style={{ padding: "8px", fontFamily: MONO, fontSize: 12 }}>{r.sku_id}</td>
                        <td style={{ padding: "8px", color: C.z500 }}>{r.brand || "—"}</td>
                        <td style={{ padding: "8px", textAlign: "right", fontFamily: MONO }}>{fmt(r.ready)}</td>
                        <td style={{ padding: "8px", textAlign: "right", fontFamily: MONO, color: C.orange, fontWeight: 600 }}>{fmt(r.D)}</td>
                        <td style={{ padding: "8px", textAlign: "right", fontFamily: MONO, fontWeight: 600 }}>{fmt(r.awaiting)}</td>
                        <td style={{ padding: "8px", textAlign: "right", fontFamily: MONO, color: C.green }}>{fmt(r.I)}</td>
                        <td style={{ padding: "8px", textAlign: "right", fontFamily: MONO, color: C.blue }}>{fmt(r.P)}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: C.z500, fontSize: 12 }}>{r.branch || "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.z200}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setPoolOpen(false)}
                data-testid="pulse-pool-close"
                style={{ padding: "8px 16px", border: `1px solid ${C.z300}`, borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13 }}
              >Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Key-value row helper (used in Leakage section). */
const KV = ({ k, v, color = C.ink, raw = false, ...rest }) => (
  <div
    style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "8px 0", borderBottom: `1px dashed ${C.z150}`, fontSize: 13,
    }}
    {...rest}
  >
    <span style={{ color: C.ink }}>{k}</span>
    <span style={{ fontFamily: MONO, color, fontWeight: 500 }}>
      {raw ? v : fmt(v)}
    </span>
  </div>
);
